import { API_BASE_URL, getAuthHeaders } from '../client';

export interface PaidPendingApplication {
  transaction_id: number;
  loan_id: number;
  applicant_id: string;
  applicant_name: string;
  amount?: number;
  amount_collected: number;
  paid_at?: string | null;
  payment_date: string | null;
  cash_in_hand?: number;
  transaction_status?: string | null;
  approval_status_id?: number | null;
  approval_status?: string | null;
  payment_mode?: string | null;
  rm_name?: string | null;
  tl_name?: string | null;
  branch?: string | null;
  lender?: string | null;
  loan_type?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  verified_by?: number | null;
  verified_at?: string | null;
  reversed_at?: string | null;
  has_receipt?: boolean;
  receipt_urls?: string[];
  reject_comment?: string | null;
  /** Same UUID for all txns in one Multiple Cash Deposit; null for single deposits */
  deposit_batch_id?: string | null;
}

export interface PaidPendingApplicationsResponse {
  total_applications: number;
  total_pages?: number;
  status: string;
  applications: PaidPendingApplication[];
  /** Optional pagination echo from API */
  limit?: number;
  offset?: number;
}

export interface PaidPendingListFilters {
  branch?: string;
  emi_month?: string;
  rm_name?: string;
  tl_name?: string;
  lender?: string;
  /** Inclusive created_at start, YYYY-MM-DD. Omit when empty. */
  from_date?: string;
  /** Inclusive created_at end, YYYY-MM-DD. Omit when empty. */
  to_date?: string;
  /**
   * Name fragment or applicant ID(s).
   * Multi-ID: comma-separated, e.g. "PROSAPP…003,PROSAPP…008"
   * (BE must OR-match each term)
   */
  search?: string;
  /** pending (default) | rejected | pending,rejected */
  approval_status?: string;
  /** all (default) | multiple | single. Applied on the server before paging. */
  deposit_type?: 'all' | 'multiple' | 'single';
  /** Page size */
  limit?: number;
  /** Skip count (offset) */
  offset?: number;
}

export interface PaidPendingApprovalRequest {
  transaction_id?: number;
  transaction_ids?: number[];
  action: 'accept' | 'reject';
  comments?: string;
}

export interface PaidPendingApprovalResultItem {
  transaction_id: number;
  success: boolean;
  new_approval_status?: string;
  message?: string;
  error?: string;
}

export interface PaidPendingApprovalResponse {
  action: string;
  total?: number;
  succeeded?: number;
  failed?: number;
  results?: PaidPendingApprovalResultItem[];
  transaction_id?: number;
  loan_id?: number;
  previous_approval_status?: string;
  new_approval_status?: string;
  message: string;
  verified_by?: number | null;
  verified_at?: string | null;
  comments?: string | null;
  reversal?: unknown;
}

async function readErrorDetail(response: Response): Promise<string> {
  const data = await response.json().catch(() => ({}));
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.message === 'string') return data.message;
  return `Request failed: ${response.status}`;
}

export class PaidPendingService {
  static async getPaidPendingApplications(
    filters: PaidPendingListFilters = {}
  ): Promise<PaidPendingApplicationsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('approval_status', filters.approval_status || 'pending');
    if (filters.branch) queryParams.append('branch', filters.branch);
    if (filters.emi_month) queryParams.append('emi_month', filters.emi_month);
    if (filters.rm_name) queryParams.append('rm_name', filters.rm_name);
    if (filters.tl_name) queryParams.append('tl_name', filters.tl_name);
    if (filters.lender) queryParams.append('lender', filters.lender);
    if (filters.from_date) queryParams.append('from_date', filters.from_date);
    if (filters.to_date) queryParams.append('to_date', filters.to_date);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.deposit_type && filters.deposit_type !== 'all') {
      queryParams.append('deposit_type', filters.deposit_type);
    }
    if (filters.limit != null) queryParams.append('limit', String(filters.limit));
    if (filters.offset != null) queryParams.append('offset', String(filters.offset));

    const response = await fetch(`${API_BASE_URL}/paidpending-approval/?${queryParams.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await readErrorDetail(response));
    }

    return await response.json();
  }

  static async getPaidPendingApplicationStatus(loanId: string): Promise<{
    loan_id: number;
    is_paid_pending: boolean;
    total_pending: number;
    applications: PaidPendingApplication[];
  }> {
    const response = await fetch(`${API_BASE_URL}/paidpending-approval/${loanId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await readErrorDetail(response));
    }

    return await response.json();
  }

  static async approveRejectPaidPending(
    approvalData: PaidPendingApprovalRequest
  ): Promise<PaidPendingApprovalResponse> {
    const body: PaidPendingApprovalRequest = {
      action: approvalData.action,
    };
    if (approvalData.transaction_id != null) {
      body.transaction_id = approvalData.transaction_id;
    }
    if (approvalData.transaction_ids?.length) {
      body.transaction_ids = approvalData.transaction_ids;
    }
    if (approvalData.action === 'reject') {
      const comments = approvalData.comments?.trim() ?? '';
      if (!comments) {
        throw new Error('Reject comment is required');
      }
      body.comments = comments;
    }

    const response = await fetch(`${API_BASE_URL}/paidpending-approval/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await readErrorDetail(response));
    }

    return await response.json();
  }

  static async isApplicationPaidPending(loanId: string): Promise<boolean> {
    try {
      const status = await this.getPaidPendingApplicationStatus(loanId);
      return status.is_paid_pending;
    } catch {
      return false;
    }
  }
}
